import React from 'react';
import Head from 'next/head';
import clsx from 'clsx';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Button from '@/components/Button';
import Text from '@/components/Text';
import { StoryContainer } from './StoryContainer';
import * as Slides from './components';

interface GeneratedLessonViewerProps {
  lessonItems: any[];
  lessonTitle: string;
  stockName: string;
  ticker?: string;
  isIndustry?: boolean;
  chapterNumber: number;
  onClose: () => void;
  onComplete: () => void;
  isFinalLesson?: boolean;
}

const CHAPTER_BACKGROUNDS = [
  'https://cdn.bloomapp.com/learn/investing-next-bg-2.png',
  'https://cdn.bloomapp.com/learn/picking-next-bg.png',
  'https://cdn.bloomapp.com/learn/anal-next-bg.png',
  'https://cdn.bloomapp.com/learn/finance-next-bg.png',
  'https://cdn.bloomapp.com/learn/crypto-next-bg.png',
  'https://cdn.bloomapp.com/learn/company-next-bg.png',
  'https://cdn.bloomapp.com/learn/market-next-bg.png',
  'https://cdn.bloomapp.com/learn/assets-next-bg.png',
  'https://cdn.bloomapp.com/learn/stats-next-bg-2.png',
  'https://cdn.bloomapp.com/learn/pro-next-bg-2.png',
];

const LANDSCAPE_BACKGROUNDS = [
  '/images/ai-lesson-images/investing-journey-landscape.svg',
  '/images/ai-lesson-images/picking-journey-landscape.svg',
  '/images/ai-lesson-images/anal-journey-landscape.svg',
  '/images/ai-lesson-images/finance-journey-landscape.svg',
  '/images/ai-lesson-images/crypto-journey-landscape.svg',
  '/images/ai-lesson-images/company-journey-landscape.svg',
  '/images/ai-lesson-images/market-journey-landscape.svg',
  '/images/ai-lesson-images/assets-journey-landscape.svg',
  '/images/ai-lesson-images/stats-journey-landscape.svg',
  '/images/ai-lesson-images/pro-journey-landscape.svg',
];

export default function GeneratedLessonViewer({
  lessonItems,
  lessonTitle,
  stockName,
  ticker,
  isIndustry,
  chapterNumber,
  onClose,
  onComplete,
  isFinalLesson = false
}: GeneratedLessonViewerProps) {
  
  // Filter out invalid slides and add completion slide
  const contentSlides = lessonItems.map((item, index) => ({
    id: `item-${index}`,
    ...item,
  }));

  const slides = [
    ...contentSlides,
    {
      id: 'completion',
      type: 'completion',
      data: {},
      onComplete: onComplete, // Pass handler to the slide
      isFinalLesson: isFinalLesson,
    },
  ];

  const firstTextSlide = contentSlides.find(s => s.type === 'text');

  const renderSlide = (slide: any) => {
    const slideProps: Slides.SlideProps = {
      slide,
      firstTextSlide,
      stockName,
      isIndustry,
      ticker,
    };

    switch (slide.type) {
      case 'text':
        return <Slides.TextSlide {...slideProps} />;
      case 'table':
        return <Slides.TableSlide {...slideProps} />;
      case 'list':
        return <Slides.ListSlide {...slideProps} />;
      case 'swot':
        return <Slides.SwotSlide {...slideProps} />;
      case 'timeline':
        return <Slides.TimelineSlide {...slideProps} />;
      case 'completion':
        return <Slides.CompletionSlide {...slideProps} isFinalLesson={slide.isFinalLesson ?? false} />;
      case 'ratio-chart':
        return <Slides.RatioChartSlide {...slideProps} />;
      case 'pie-chart':
        return <Slides.PieChartSlide {...slideProps} />;
      case 'bar-chart':
        return <Slides.BarChartSlide {...slideProps} />;
      case 'bcg-matrix':
        return <Slides.BcgMatrixSlide {...slideProps} />;
      case 'comparison':
      case 'framework':
        return <Slides.ComparisonFrameworkSlide {...slideProps} />;
      default:
        return <Slides.DefaultSlide {...slideProps} />;
    }
  };

  const landscapeImage = LANDSCAPE_BACKGROUNDS[(chapterNumber - 1) % LANDSCAPE_BACKGROUNDS.length];

  return (
    <>
      <Head>
        <title>{lessonTitle} | Bloom</title>
      </Head>

      <div className="flex flex-col gap-2 md:gap-6 w-full h-[calc(100vh-64px)]">
        {/* Header */}
        <div className="flex items-center gap-2 md:gap-4 py-1 md:py-0">
          <Button 
            type="IconButton" 
            className="bg-btn-primary flex-none !w-8 !h-8 md:!w-10 md:!h-10 !p-0 flex items-center justify-center" 
            onClick={onClose}
          >
            <FontAwesomeIcon icon={faChevronLeft} className="w-2.5 h-2.5 md:w-3 md:h-3 text-white flex-shrink-0" />
          </Button>
          <div className="flex flex-col min-w-0 flex-1">
            <Text size="xs" className="text-white/40 uppercase tracking-widest text-[9px] md:text-xs">
              {stockName} Lesson
            </Text>
            <h1 className="text-sm md:text-lg text-white leading-tight truncate">
              {lessonTitle}
            </h1>
          </div>
        </div>

        {/* Content Area */}
        <div className={clsx(
          "relative flex flex-col w-full rounded-2xl overflow-hidden shadow-2xl flex-1",
          chapterNumber % 3 === 1 ? 'bg-gradient-blue' :
          chapterNumber % 3 === 2 ? 'bg-gradient-blue-pink' :
          'bg-gradient-blue-purple'
        )}>
          <StoryContainer 
            slides={slides} 
            onComplete={onComplete} 
            renderSlide={renderSlide}
            duration={8000} // 8 seconds per slide
            backgroundImage={landscapeImage}
          />
        </div>
      </div>
    </>
  );
}
